import React, { useMemo, useState } from "react";
import { GripVertical, IndianRupee } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { money, relative } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/common/UserAvatar";
import { cn } from "@/lib/utils";

const COLUMNS = [
  { key: "New Inquiry", accent: "var(--chart-2)" },
  { key: "Contacted", accent: "var(--chart-2)" },
  { key: "Follow-up Required", accent: "var(--chart-4)" },
  { key: "Interested", accent: "var(--chart-1)" },
  { key: "Quotation Created", accent: "var(--chart-1)" },
  { key: "Negotiation", accent: "var(--chart-4)" },
  { key: "Proforma Invoice Created", accent: "var(--chart-5)" },
  { key: "Invoice Created", accent: "var(--chart-3)" },
  { key: "Converted", accent: "var(--chart-3)" },
  { key: "On Hold", accent: "var(--muted-foreground)" },
  { key: "Lost", accent: "var(--destructive)" },
];

export const PipelineBoard = ({ leads, onOpen, onChanged }) => {
  const { can } = useAuth();
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const editable = can("crm", "edit");

  const grouped = useMemo(() => {
    const g = {};
    COLUMNS.forEach((c) => { g[c.key] = []; });
    leads.forEach((l) => { (g[l.status] = g[l.status] || []).push(l); });
    return g;
  }, [leads]);

  const drop = async (status) => {
    setOverCol(null);
    const lead = leads.find((l) => l.id === dragId);
    setDragId(null);
    if (!lead || lead.status === status) return;
    try {
      await api.patch(`/leads/${lead.id}`, { status });
      toast.success(`${lead.customer_name} moved to ${status}`);
      onChanged?.();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const visible = COLUMNS.filter((c) => grouped[c.key]?.length > 0 || ["New Inquiry", "Contacted", "Interested", "Quotation Created"].includes(c.key));

  return (
    <div className="scroll-slim -mx-1 overflow-x-auto px-1 pb-3">
      <div className="flex gap-4" style={{ minWidth: `${visible.length * 272}px` }}>
        {visible.map((col) => {
          const items = grouped[col.key] || [];
          const value = items.reduce((a, l) => a + (l.value || 0), 0);
          return (
            <div
              key={col.key}
              onDragOver={(e) => { if (editable) { e.preventDefault(); setOverCol(col.key); } }}
              onDragLeave={() => setOverCol((c) => (c === col.key ? null : c))}
              onDrop={() => editable && drop(col.key)}
              data-testid={`col-${col.key}`}
              className={cn(
                "flex w-[256px] shrink-0 flex-col rounded-xl border bg-muted/40 transition-colors",
                overCol === col.key ? "border-primary bg-primary-soft/50" : "border-border"
              )}
            >
              <div className="flex items-center gap-2 border-b border-border/70 px-3 py-2.5">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: `hsl(${col.accent})` }} />
                <span className="min-w-0 flex-1 truncate text-xs font-semibold uppercase tracking-wide">{col.key}</span>
                <span className="num rounded-full bg-card px-2 py-0.5 text-[11px] font-bold">{items.length}</span>
              </div>
              {value > 0 && (
                <p className="num flex items-center gap-1 px-3 pt-2 text-[11px] text-muted-foreground">
                  <IndianRupee className="h-3 w-3" />{money(value, true).replace("\u20b9", "")}
                </p>
              )}

              <div className="flex flex-1 flex-col gap-2 p-2">
                {items.length === 0 && (
                  <p className="rounded-lg border border-dashed border-border px-3 py-6 text-center text-[11px] text-muted-foreground">
                    {editable ? "Drop a lead here" : "Nothing here"}
                  </p>
                )}
                {items.map((l) => (
                  <div
                    key={l.id}
                    draggable={editable}
                    onDragStart={() => setDragId(l.id)}
                    onDragEnd={() => setDragId(null)}
                    onClick={() => onOpen(l.id)}
                    className={cn(
                      "group cursor-pointer rounded-lg border border-border bg-card p-3 shadow-xs transition-all",
                      "hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md",
                      dragId === l.id && "opacity-40"
                    )}
                  >
                    <div className="flex items-start gap-2">
                      <UserAvatar name={l.customer_name} size="sm" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold">{l.customer_name}</p>
                        <p className="truncate text-[11px] text-muted-foreground">{l.company || l.phone}</p>
                      </div>
                      {editable && <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground/40 group-hover:text-muted-foreground" />}
                    </div>
                    {l.product && <p className="mt-2 truncate text-[11px] text-muted-foreground">{l.product}</p>}
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <span className="num text-xs font-bold text-primary">{money(l.value)}</span>
                      <span className="truncate text-[10px] text-muted-foreground">{relative(l.created_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
